import { FlagOutlined } from "@mui/icons-material";
import { Box, Button, Card, CardContent, CardHeader, Divider, Stack, styled, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import _, { Dictionary } from "lodash";
import { useCallback, useEffect, useState } from "react";
import {
  AccordionViewList,
  CreateIssueModal,
  filterIssuesFn,
  Issue,
  IssueDrawerContent,
  IssueListItemSkeleton,
  IssuesTable,
  ItemDrawerTitle,
  SignalRGroup,
  sortIssuesFn,
  useDrawer,
  ViewListColor,
} from "../../../../components";
import {
  ClosedIssueStatuses,
  ISSUE_STATUSES,
  IssuesFilterByOption,
  Links,
  TeamIssuesGroupByOption,
} from "../../../../constants";
import { useSession, useSignalR } from "../../../../hooks";
import { IssueSummaryDto, queryKeys, useGetIssues, useGetTeam } from "../../../../http";
import { useTeamImplementationSettings } from "../hooks";
import { TeamIssuesFilterButton } from "./TeamIssuesFilterButton";

const AccordionContainer = styled(Box)`
  background-color: #fef5ed;
  border-color: #fad3b0;
  border-style: dashed;
  border-width: 1px;
  border-radius: 10px;
`;

interface IProps {
  teamSlug: string;
  isTableView: boolean;
}

const TeamIssuesList = (props: IProps) => {
  const { userId: sessionUserGuid } = useSession();
  const { settings } = useTeamImplementationSettings(props.teamSlug);
  const { data: team } = useGetTeam(props.teamSlug, sessionUserGuid);
  const { data, isLoading } = useGetIssues(props.teamSlug, undefined);
  const [groupedIssues, setGroupedIssues] = useState<Dictionary<IssueSummaryDto[]> | undefined>(undefined);
  const [isCreateIssueModalOpen, setIsCreateIssueModalOpen] = useState(false);
  const { showDrawer } = useDrawer();
  const signalR = useSignalR();
  const queryClient = useQueryClient();

  useEffect(() => {
    signalR.joinGroup(SignalRGroup.TeamIssues(props.teamSlug));
    return () => signalR.leaveGroup(SignalRGroup.TeamIssues(props.teamSlug));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.teamSlug]);

  useEffect(() => {
    const handleShouldRefetchTeamIssues = (teamSlug: string) => {
      queryClient.invalidateQueries(queryKeys.issues.getIssues(teamSlug, undefined));
    };

    signalR.onShouldRefetchTeamIssues(handleShouldRefetchTeamIssues);
    return () => signalR.offShouldRefetchTeamIssues(handleShouldRefetchTeamIssues);
  }, [queryClient, signalR]);

  const groupByFunction = useCallback(
    (issue: IssueSummaryDto) => {
      switch (settings.issuesGroupByOption) {
        case TeamIssuesGroupByOption.Status:
          return ISSUE_STATUSES.find((x) => x.id === issue.currentStatus)?.name;
        case TeamIssuesGroupByOption.CreatedBy:
          return issue.createdByUser.displayName;
        case TeamIssuesGroupByOption.None:
        default:
          return "All";
      }
    },
    [settings.issuesGroupByOption]
  );

  const groupSortByFunction = useCallback(
    (issue: IssueSummaryDto) => {
      switch (settings.issuesGroupByOption) {
        case TeamIssuesGroupByOption.Status:
          return ISSUE_STATUSES.find((x) => x.id === issue.currentStatus)?.sortOrder;
        case TeamIssuesGroupByOption.CreatedBy:
          return issue.createdByUser.displayName;
        case TeamIssuesGroupByOption.None:
        default:
          return;
      }
    },
    [settings.issuesGroupByOption]
  );

  const filterByFunction = useCallback(
    (issue: IssueSummaryDto) => {
      return filterIssuesFn(settings.issuesFilterByOption, settings.issuesFilterByStatusOption, issue);
    },
    [settings.issuesFilterByOption, settings.issuesFilterByStatusOption]
  );

  function sortByFunction(a: IssueSummaryDto, b: IssueSummaryDto) {
    return sortIssuesFn(settings.issuesSortByOption, settings.issuesSortByDirection, a, b);
  }

  useEffect(() => {
    if (data !== undefined) {
      const issues = _.groupBy(_.sortBy(data.issues.filter(filterByFunction), groupSortByFunction), groupByFunction);
      setGroupedIssues(issues);
    }
  }, [data, groupByFunction, groupSortByFunction, filterByFunction]);

  async function handleIssueClicked(issue: IssueSummaryDto) {
    showDrawer({
      title: <ItemDrawerTitle link={Links.IssueDetail(issue.guid)} />,
      content: <IssueDrawerContent issueGuid={issue.guid} />,
    });
  }

  return (
    <Card>
      <CardHeader
        direction="row"
        title={<Typography variant="h6">Issues</Typography>}
        action={
          <Stack direction="row" spacing={1} divider={<Divider orientation="vertical" flexItem />}>
            <Button
              variant="tertiary"
              disabled={isLoading}
              startIcon={<FlagOutlined />}
              onClick={() => setIsCreateIssueModalOpen(true)}
            >
              Add New Issue
            </Button>
            <TeamIssuesFilterButton teamSlug={props.teamSlug} disabled={isLoading} />
          </Stack>
        }
      />
      <Divider />
      <CardContent>
        {isLoading || team === undefined || groupedIssues === undefined ? (
          <AccordionContainer>
            <Stack spacing={0.5} sx={{ mx: 1, my: 0.5 }} divider={<Divider variant="dashed" />}>
              {[...Array(3)].map((_, i) => (
                <IssueListItemSkeleton key={i} />
              ))}
            </Stack>
          </AccordionContainer>
        ) : (
          <Stack spacing={1}>
            {_.keys(groupedIssues).length === 0 ? (
              <Typography>Nothing here yet.</Typography>
            ) : (
              _.keys(groupedIssues).map((groupName, i) => (
                <AccordionContainer key={i}>
                  <AccordionViewList
                    title={groupName}
                    isLoading={isLoading}
                    dataSource={groupedIssues[groupName]}
                    divider={<Divider variant="dashed" sx={{ mx: 1 }} />}
                    renderContent={
                      props.isTableView
                        ? (issues) => (
                            <IssuesTable
                              tableId={`teamIssuesList-${team.slug}`}
                              issues={issues.sort(sortByFunction)}
                              enableActions={team.isAdministrator || team.isMember}
                            />
                          )
                        : undefined
                    }
                    renderItem={(issue) => (
                      <Issue
                        key={issue.guid}
                        issue={issue}
                        onClick={() => handleIssueClicked(issue)}
                        isFocused={false}
                        sx={{ px: 1 }}
                      />
                    )}
                    color={ViewListColor.Orange}
                    activeItemsListProps={{
                      activeItemsSortFunction: sortByFunction,
                      divider: <Divider variant="dashed" sx={{ mx: 1 }} />,
                    }}
                    closedItemsListProps={
                      settings.issuesGroupByOption !== TeamIssuesGroupByOption.Status &&
                      settings.issuesFilterByOption !== IssuesFilterByOption.Status
                        ? {
                            closedItemsFilter: (item) => ClosedIssueStatuses.includes(item.currentStatus),
                            closedItemsSortFunction: sortByFunction,
                          }
                        : undefined
                    }
                  />
                </AccordionContainer>
              ))
            )}
          </Stack>
        )}
      </CardContent>

      <CreateIssueModal
        visible={isCreateIssueModalOpen}
        onCreated={() => setIsCreateIssueModalOpen(false)}
        onCancelled={() => setIsCreateIssueModalOpen(false)}
        defaultSelectedTeamSlug={props.teamSlug}
      />
    </Card>
  );
};

export { TeamIssuesList };
